import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { money, centsToInput, parseMoneyToCents } from "../lib/format";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const NEEDS = {
  percentage_billings: ["billings"],
  percentage_collections: ["collections"],
  percentage_after_lab: ["billings", "lab"],
  hourly: ["time"],
  daily: ["days"],
  fixed: [],
  custom: ["custom"],
};

function hoursFromTimes(start, finish, brk) {
  if (!start || !finish) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [fh, fm] = finish.split(":").map(Number);
  let mins = fh * 60 + fm - (sh * 60 + sm) - (parseInt(brk) || 0);
  if (mins <= 0) return null;
  return Math.round((mins / 60) * 100) / 100;
}

export default function AddWork() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const qc = useQueryClient();
  const editing = location.state?.entry;

  const { data: practices } = useQuery({
    queryKey: ["practices-min"],
    queryFn: async () => (await api.get(`/practices`)).data,
    // Local SQLite is the source offline; keep the last good list so a connectivity change
    // or a failed remote refresh never replaces valid local practices with an empty list.
    networkMode: "always",
    placeholderData: keepPreviousData,
  });

  const [form, setForm] = useState({
    practice_id: editing?.practice_id || "",
    date: editing?.date || new Date().toLocaleDateString("en-CA"),
    start_time: editing?.start_time || "08:00",
    finish_time: editing?.finish_time || "17:00",
    break_minutes: editing?.break_minutes || 0,
    billings: editing ? centsToInput(editing.billings_cents) : "",
    collections: editing ? centsToInput(editing.collections_cents) : "",
    lab: editing ? centsToInput(editing.lab_fees_cents) : "",
    other: editing ? centsToInput(editing.other_deductions_cents) : "",
    custom: editing ? centsToInput(editing.snapshot?.expected_cents) : "",
    days: editing?.days || 1,
    notes: editing?.notes || "",
    gst_taxable: editing?.gst_taxable || false,
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => (practices || []).find((p) => p.id === form.practice_id),
    [practices, form.practice_id]
  );
  const method = selected?.agreement?.calc_method;
  const needs = NEEDS[method] || [];
  const hours = hoursFromTimes(form.start_time, form.finish_time, form.break_minutes);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildBody = () => ({
    practice_id: form.practice_id,
    date: form.date,
    start_time: form.start_time,
    finish_time: form.finish_time,
    break_minutes: parseInt(form.break_minutes) || 0,
    notes: form.notes,
    billings_cents: parseMoneyToCents(form.billings),
    collections_cents: parseMoneyToCents(form.collections),
    lab_fees_cents: parseMoneyToCents(form.lab),
    other_deductions_cents: parseMoneyToCents(form.other),
    hours,
    days: parseFloat(form.days) || 0,
    custom_expected_cents: parseMoneyToCents(form.custom),
    gst_taxable: form.gst_taxable,
  });

  useEffect(() => {
    if (!form.practice_id) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/work/preview", buildBody());
        setPreview(data);
      } catch { setPreview(null); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form]);

  const save = async () => {
    if (!form.practice_id) return toast.error("Please choose a practice.");
    setSaving(true);
    try {
      if (editing) await api.put(`/work/${params.id}`, buildBody());
      else await api.post("/work", buildBody());
      // refetchType:"all" also refetches the (currently inactive) Work list query from the
      // local repository, so a new record appears immediately after navigation — even fully
      // offline, with no force-close/relaunch and no network dependency.
      await qc.invalidateQueries({ refetchType: "all" });
      toast.success(editing ? "Work updated" : "Work saved");
      navigate("/work");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const remove = async () => {
    await api.delete(`/work/${params.id}`);
    await qc.invalidateQueries({ refetchType: "all" });
    toast.success("Work deleted");
    navigate("/work");
  };

  const snap = preview?.snapshot;

  return (
    <AppShell>
      <PageHeader title={editing ? "Edit Work" : "Add Work"} onBack={() => navigate(-1)} />
      <div className="px-5 space-y-4 pb-40 df-fade-in">
        <div className="df-card p-4 space-y-4">
          <div>
            <Label className="text-slate-700">Practice</Label>
            <Select value={form.practice_id} onValueChange={(v) => set("practice_id", v)}>
              <SelectTrigger data-testid="work-practice-select" className="mt-1.5 h-12 rounded-xl bg-white">
                <SelectValue placeholder="Choose a practice" />
              </SelectTrigger>
              <SelectContent>
                {(practices || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}{p.suburb ? ` — ${p.suburb}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && <p className="text-xs text-slate-500 mt-1.5">{selected.method_label} · {selected.rate_label}</p>}
          </div>

          <div>
            <Label className="text-slate-700">Date</Label>
            <div className="mt-1.5 w-full min-w-0 max-w-full overflow-hidden rounded-xl">
              <Input type="date" data-testid="work-date" value={form.date}
                onChange={(e) => set("date", e.target.value)}
                className="h-12 rounded-xl bg-white block w-full min-w-0 max-w-full box-border appearance-none" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="min-w-0">
              <Label className="text-slate-700 text-xs">Start</Label>
              <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)}
                className="mt-1.5 h-12 rounded-xl bg-white w-full min-w-0 max-w-full box-border px-2" data-testid="work-start" />
            </div>
            <div className="min-w-0">
              <Label className="text-slate-700 text-xs">Finish</Label>
              <Input type="time" value={form.finish_time} onChange={(e) => set("finish_time", e.target.value)}
                className="mt-1.5 h-12 rounded-xl bg-white w-full min-w-0 max-w-full box-border px-2" data-testid="work-finish" />
            </div>
            <div className="min-w-0">
              <Label className="text-slate-700 text-xs">Break (min)</Label>
              <Input type="number" inputMode="numeric" value={form.break_minutes}
                onChange={(e) => set("break_minutes", e.target.value)}
                className="mt-1.5 h-12 rounded-xl bg-white w-full min-w-0 max-w-full box-border px-2" data-testid="work-break" />
            </div>
          </div>
          {needs.includes("time") && (
            <p className="text-xs text-slate-500">Hours worked: <b className="font-num">{hours ?? "—"}</b></p>
          )}
        </div>

        {form.practice_id && (
          <div className="df-card p-4 space-y-4">
            <div className="text-sm font-semibold text-slate-900">Financial details</div>
            {needs.includes("billings") && (
              <MoneyField label="Billings / Production" testId="work-billings" value={form.billings} onChange={(v) => set("billings", v)} />
            )}
            {needs.includes("collections") && (
              <MoneyField label="Collections" testId="work-collections" value={form.collections} onChange={(v) => set("collections", v)} />
            )}
            {needs.includes("lab") && (
              <MoneyField label="Lab fees" testId="work-lab" value={form.lab} onChange={(v) => set("lab", v)} />
            )}
            {needs.includes("days") && (
              <div>
                <Label className="text-slate-700">Days worked</Label>
                <Input type="number" step="0.5" inputMode="decimal" value={form.days}
                  onChange={(e) => set("days", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white font-num" data-testid="work-days" />
              </div>
            )}
            {needs.includes("custom") && (
              <MoneyField label="Expected payment (custom)" testId="work-custom" value={form.custom} onChange={(v) => set("custom", v)} />
            )}
            {method === "fixed" && <p className="text-sm text-slate-500">This agreement pays a fixed amount per work day.</p>}
            <MoneyField label="Other agreed deductions (optional)" testId="work-other" value={form.other} onChange={(v) => set("other", v)} />
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-slate-700">Income is GST taxable</Label>
                <p className="text-[11px] text-slate-400">Most dental services are GST-free.</p>
              </div>
              <Switch data-testid="work-gst-taxable" checked={form.gst_taxable} onCheckedChange={(v) => set("gst_taxable", v)} />
            </div>
          </div>
        )}

        {snap && (
          <div className="df-card p-5 border-[#A7F3D0] bg-[#F4FBF9]" data-testid="expected-earnings-card">
            <div className="text-xs font-semibold tracking-wider uppercase text-[#0D786A] mb-3">Expected Earnings</div>
            <Breakdown snap={snap} />
            <div className="mt-4 pt-4 border-t border-emerald-100">
              <div className="text-sm text-slate-500">Expected payment</div>
              <div className="font-num font-bold text-3xl text-emerald-900" data-testid="preview-expected">
                {money(snap.expected_cents)}
              </div>
              {selected && <div className="text-xs text-slate-500 mt-1">Based on your {selected.name} agreement (v{preview.agreement_version_number})</div>}
            </div>
          </div>
        )}
      </div>

      <div className="df-actionbar">
        {editing && (
          <Button variant="outline" data-testid="work-delete" onClick={remove}
            className="h-12 rounded-xl border-red-200 text-red-600 hover:bg-red-50 px-4">
            <Trash2 size={18} />
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate(-1)} className="flex-1 h-12 rounded-xl">Cancel</Button>
        <Button data-testid="work-save" onClick={save} disabled={saving}
          className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {saving ? "Saving…" : "Save Work"}
        </Button>
      </div>
    </AppShell>
  );
}

function MoneyField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="relative mt-1.5">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
        <Input type="number" step="0.01" inputMode="decimal" data-testid={testId} value={value}
          onChange={(e) => onChange(e.target.value)} placeholder="0.00"
          className="h-12 rounded-xl bg-white pl-7 font-num" />
      </div>
    </div>
  );
}

function Row({ label, value, strong, negative }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={`text-sm ${strong ? "text-slate-900 font-medium" : "text-slate-500"}`}>{label}</span>
      <span className={`font-num ${strong ? "font-semibold text-slate-900" : "text-slate-700"} ${negative ? "text-red-600" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function Breakdown({ snap }) {
  const m = snap.calc_method;
  return (
    <div className="space-y-0.5">
      {snap.revenue_basis_cents != null && (
        <Row label={snap.revenue_basis_label} value={money(snap.revenue_basis_cents)} />
      )}
      {m === "percentage_after_lab" && (
        <>
          <Row label="Lab fees" value={money(snap.lab_fees_cents)} />
          {snap.dentist_lab_share_bps != null && (
            <Row label="Your agreed lab share" value={`${snap.dentist_lab_share_bps / 100}%`} />
          )}
          <Row label="Your lab deduction" value={`−${money(snap.dentist_lab_deduction_cents)}`} negative />
        </>
      )}
      {snap.other_deductions_cents > 0 && (
        <Row label="Other deductions" value={`−${money(snap.other_deductions_cents)}`} negative />
      )}
      {snap.calculation_base_cents != null && (
        <Row label="Calculation base" value={money(snap.calculation_base_cents)} strong />
      )}
      {snap.percentage_bps != null && <Row label="Agreement" value={`${snap.percentage_bps / 100}%`} />}
      {snap.rate_cents != null && (
        <Row label={`Rate × ${snap.quantity}`} value={money(snap.rate_cents)} />
      )}
    </div>
  );
}
